export const articleWarehouse = {
  slug: 'usfans-warehouse',
  en: {
    title: 'USFans Warehouse Guide: How Storage and Order Handling Work',
    description:
      'Understand what a warehouse does in the order process, from storage and packing to shipping, in plain language.',
    tag: 'Warehouse',
    intro: [
      'A warehouse is the part of the order process where items are stored, picked and packed before they are shipped. Knowing how this works helps you understand where your order is and why timelines vary.',
      'This guide explains the warehouse role in simple terms, without claiming to know internal operations that are not public.',
    ],
    sections: [
      {
        title: 'What a warehouse does',
        paragraphs: [
          'A warehouse receives and stores inventory, then picks the items for each order and packs them for shipping. This step happens between checkout and the courier picking up the package.',
          'Storage conditions and organization affect how quickly orders are processed and how items are handled.',
        ],
      },
      {
        title: 'Why processing time varies',
        paragraphs: [
          'Processing time depends on order volume, item availability and how quickly items can be located and packed. Busy periods can add delays.',
          'Tracking usually updates once the package is handed to the courier, which is why there can be a gap between ordering and the first tracking event.',
        ],
      },
      {
        title: 'What this means for you',
        paragraphs: [
          'Expect a processing window before shipping, and check your order details for any updates. Contact the official support channel if an order appears stuck for an unusually long time.',
          'Keep your confirmation details handy so you can reference your order number when needed.',
        ],
      },
    ],
    faq: [
      { q: 'What does a USFans warehouse do?', a: 'It stores inventory, picks items for each order and packs them for shipping, between checkout and courier pickup.' },
      { q: 'Why does processing time vary?', a: 'It depends on order volume, item availability and how quickly items can be located and packed.' },
      { q: 'When does tracking start?', a: 'Tracking usually updates once the package is handed to the courier, so there can be a gap after ordering.' },
    ],
  },
  pl: {
    title: 'USFans Warehouse: jak działa przechowywanie i obsługa zamówień',
    description:
      'Zrozum prostym językiem, co robi magazyn w procesie zamówienia: od przechowywania i pakowania po wysyłkę.',
    tag: 'Magazyn',
    intro: [
      'Magazyn to część procesu zamówienia, w której produkty są przechowywane, wybierane i pakowane przed wysyłką. Wiedza o tym pomaga zrozumieć, gdzie jest zamówienie i dlaczego czasy się różnią.',
      'Ten przewodnik wyjaśnia rolę magazynu prostymi słowami, bez twierdzeń o wewnętrznych procesach, które nie są publiczne.',
    ],
    sections: [
      {
        title: 'Co robi magazyn',
        paragraphs: [
          'Magazyn przyjmuje i przechowuje zapasy, a następnie wybiera produkty do każdego zamówienia i pakuje je do wysyłki. Ten etap dzieje się między kasą a odbiorem paczki przez kuriera.',
          'Warunki przechowywania i organizacja wpływają na szybkość obsługi zamówień i sposób obchodzenia się z produktami.',
        ],
      },
      {
        title: 'Dlaczego czas przetwarzania się różni',
        paragraphs: [
          'Czas przetwarzania zależy od liczby zamówień, dostępności produktów i szybkości lokalizowania oraz pakowania. Okresy wzmożonego ruchu mogą dodawać opóźnienia.',
          'Śledzenie zwykle aktualizuje się, gdy paczka trafi do kuriera, dlatego między zamówieniem a pierwszym zdarzeniem śledzenia może być przerwa.',
        ],
      },
      {
        title: 'Co to oznacza dla Ciebie',
        paragraphs: [
          'Spodziewaj się okna przetwarzania przed wysyłką i sprawdzaj szczegóły zamówienia pod kątem aktualizacji. Skontaktuj się z oficjalnym kanałem wsparcia, jeśli zamówienie utknęło na wyjątkowo długo.',
          'Miej pod ręką dane potwierdzenia, aby w razie potrzeby podać numer zamówienia.',
        ],
      },
    ],
    faq: [
      { q: 'Co robi magazyn USFans?', a: 'Przechowuje zapasy, wybiera produkty do każdego zamówienia i pakuje je do wysyłki, między kasą a odbiorem kuriera.' },
      { q: 'Dlaczego czas przetwarzania się różni?', a: 'Zależy od liczby zamówień, dostępności produktów i szybkości lokalizowania oraz pakowania.' },
      { q: 'Kiedy zaczyna się śledzenie?', a: 'Śledzenie zwykle aktualizuje się, gdy paczka trafi do kuriera, więc po zamówieniu może być przerwa.' },
    ],
  },
  es: {
    title: 'Guía del almacén de USFans: cómo funcionan el almacenamiento y el manejo de pedidos',
    description:
      'Entiende qué hace un almacén en el proceso de pedido, del almacenamiento y empaque al envío, en lenguaje sencillo.',
    tag: 'Almacén',
    intro: [
      'Un almacén es la parte del proceso de pedido donde se guardan, seleccionan y empaquetan los artículos antes de enviarse. Saber cómo funciona te ayuda a entender dónde está tu pedido y por qué varían los plazos.',
      'Esta guía explica el papel del almacén en términos simples, sin afirmar conocer operaciones internas que no son públicas.',
    ],
    sections: [
      {
        title: 'Qué hace un almacén',
        paragraphs: [
          'Un almacén recibe y guarda el inventario, luego selecciona los artículos de cada pedido y los empaqueta para el envío. Este paso ocurre entre el pago y la recogida del paquete por el mensajero.',
          'Las condiciones de almacenamiento y la organización afectan la rapidez del procesamiento y el manejo de los artículos.',
        ],
      },
      {
        title: 'Por qué varía el tiempo de procesamiento',
        paragraphs: [
          'El tiempo de procesamiento depende del volumen de pedidos, la disponibilidad de artículos y la rapidez para localizar y empaquetar. Las épocas de mucha actividad añaden retrasos.',
          'El seguimiento suele actualizarse cuando el paquete se entrega al mensajero, por eso puede haber un hueco entre pedir y el primer evento de seguimiento.',
        ],
      },
      {
        title: 'Qué significa para ti',
        paragraphs: [
          'Espera un plazo de procesamiento antes del envío y revisa los detalles de tu pedido por si hay novedades. Contacta el canal de soporte oficial si un pedido parece atascado demasiado tiempo.',
          'Guarda tus datos de confirmación para poder dar tu número de pedido cuando sea necesario.',
        ],
      },
    ],
    faq: [
      { q: '¿Qué hace un almacén de USFans?', a: 'Guarda inventario, selecciona los artículos de cada pedido y los empaqueta para el envío, entre el pago y la recogida del mensajero.' },
      { q: '¿Por qué varía el tiempo de procesamiento?', a: 'Depende del volumen de pedidos, la disponibilidad y la rapidez para localizar y empaquetar.' },
      { q: '¿Cuándo empieza el seguimiento?', a: 'El seguimiento suele actualizarse al entregar el paquete al mensajero, por eso puede haber un hueco tras pedir.' },
    ],
  },
  fr: {
    title: 'Guide de l’entrepôt USFans : stockage et traitement des commandes',
    description:
      'Comprenez ce que fait un entrepôt dans le processus de commande, du stockage et de l’emballage à l’expédition, en langage simple.',
    tag: 'Entrepôt',
    intro: [
      'Un entrepôt est la partie du processus de commande où les articles sont stockés, prélevés et emballés avant l’expédition. Savoir comment cela fonctionne aide à comprendre où en est votre commande.',
      'Ce guide explique le rôle de l’entrepôt simplement, sans prétendre connaître des opérations internes non publiques.',
    ],
    sections: [
      {
        title: 'Ce que fait un entrepôt',
        paragraphs: [
          'Un entrepôt reçoit et stocke les stocks, puis prélève les articles de chaque commande et les emballe pour l’expédition. Cette étape se situe entre le paiement et la prise en charge du colis.',
          'Les conditions de stockage et l’organisation influencent la rapidité du traitement et la manipulation des articles.',
        ],
      },
      {
        title: 'Pourquoi le délai de traitement varie',
        paragraphs: [
          'Le délai dépend du volume de commandes, de la disponibilité des articles et de la rapidité de prélèvement et d’emballage. Les périodes chargées ajoutent des retards.',
          'Le suivi se met souvent à jour lorsque le colis est remis au transporteur, d’où un écart entre la commande et le premier événement de suivi.',
        ],
      },
      {
        title: 'Ce que cela signifie pour vous',
        paragraphs: [
          'Attendez-vous à un délai de traitement avant l’expédition et consultez les détails de votre commande. Contactez le support officiel si une commande semble bloquée anormalement longtemps.',
          'Gardez vos informations de confirmation à portée de main pour donner votre numéro de commande.',
        ],
      },
    ],
    faq: [
      { q: 'Que fait un entrepôt USFans ?', a: 'Il stocke les stocks, prélève les articles de chaque commande et les emballe pour l’expédition, entre le paiement et la prise en charge.' },
      { q: 'Pourquoi le délai de traitement varie ?', a: 'Il dépend du volume de commandes, de la disponibilité et de la rapidité de prélèvement et d’emballage.' },
      { q: 'Quand commence le suivi ?', a: 'Le suivi se met souvent à jour lorsque le colis est remis au transporteur, d’où un écart après la commande.' },
    ],
  },
};